import React, { useState, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Field } from '../components/ui';
import { HiOutlineCheckCircle, HiOutlineDocumentText, HiOutlinePhotograph } from 'react-icons/hi';

function FileUploadBox({ label, name, accept, file, onChange, icon: Icon }) {
  const [drag, setDrag] = useState(false);
  const ref = useRef();

  const handleDrop = e => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files[0];
    if (f) onChange({ target: { name, files: [f] } });
  };

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all duration-200 
        ${drag ? 'drag-over' : 'border-gold-500/20 hover:border-gold-500/40'}`}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      onClick={() => ref.current.click()}>
      <input ref={ref} type="file" name={name} accept={accept} className="hidden"
        onChange={onChange} />
      {file ? (
        <div className="flex items-center justify-center gap-2 text-emerald-400">
          <HiOutlineCheckCircle className="w-5 h-5" />
          <span className="text-sm truncate max-w-[200px]">{file.name}</span>
        </div>
      ) : (
        <div className="space-y-2">
          <Icon className="w-8 h-8 text-slate-400 mx-auto" />
          <div className="text-slate-500 text-sm">{label}</div>
          <div className="text-slate-400 text-xs">Drag & drop or click — Max 10MB</div>
        </div>
      )}
    </div>
  );
}

export default function EmployeeRegistry() {
  const [form, setForm] = useState({
    firstName: '', lastName: '', dateOfBirth: '', email: '',
    contactNumber: '', bloodGroup: '', residentialAddress: '',
  });
  const [files, setFiles] = useState({ aadharCard: null, passportPhoto: null });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  const handleFile = e => {
    const { name, files: f } = e.target;
    if (f[0] && f[0].size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    setFiles(prev => ({ ...prev, [name]: f[0] }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (files.aadharCard) fd.append('aadharCard', files.aadharCard);
      if (files.passportPhoto) fd.append('passportPhoto', files.passportPhoto);
      await api.post('/employees/register', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) return (
    <div className="min-h-screen bg-obsidian-950 flex items-center justify-center">
      <div className="luxury-card p-12 max-w-md w-full text-center animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
          <HiOutlineCheckCircle className="w-10 h-10 text-emerald-400" />
        </div>
        <h2 className=" text-3xl text-slate-700 mb-3">Registration Complete</h2>
        <p className="text-slate-400 text-sm mb-8">Your details have been submitted. The admin will review and assign your employee ID.</p>
        <button className="btn-gold px-8 py-3" onClick={() => { setSuccess(false); setForm({ firstName: '', lastName: '', dateOfBirth: '', email: '', contactNumber: '', bloodGroup: '', residentialAddress: '' }); setFiles({ aadharCard: null, passportPhoto: null }); }}>
          Register Another
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-1 h-8 rounded-full" style={{ background: 'linear-gradient(180deg, #d4a833, #f5d98b)' }} />
          <h1 className=" text-4xl text-slate-700">Employee Registry</h1>
        </div>
        <p className="text-slate-500 text-sm ml-4">Complete your profile to contribute to the organisation</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Personal Info */}
        <div className="luxury-card p-6 mb-5">
          <h2 className=" text-lg text-slate-800 mb-5 flex items-center gap-2">
            <span className="text-xs text-slate-800 bg-obsidian-700 px-2 py-1 rounded">01</span>
            Personal Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="First Name" required>
              <input name="firstName" value={form.firstName} onChange={handleChange}
                className="luxury-input" placeholder="John" required />
            </Field>
            <Field label="Last Name" required>
              <input name="lastName" value={form.lastName} onChange={handleChange}
                className="luxury-input" placeholder="Doe" required />
            </Field>
            <Field label="Date of Birth" required>
              <input name="dateOfBirth" type="date" value={form.dateOfBirth} onChange={handleChange}
                className="luxury-input" required />
            </Field>
            <Field label="Blood Group" required>
              <input name="bloodGroup" value={form.bloodGroup} onChange={handleChange}
                className="luxury-input" placeholder="Blood Group" required />
            </Field>
          </div>
        </div>

        {/* Contact */}
        <div className="luxury-card p-6 mb-5">
          <h2 className=" text-lg text-slate-800 mb-5 flex items-center gap-2">
            <span className="text-xs  text-slate-800 bg-obsidian-700 px-2 py-1 rounded">02</span>
            Contact Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email Address" required>
              <input name="email" type="email" value={form.email} onChange={handleChange}
                className="luxury-input" placeholder="john@company.com" required />
            </Field>
            <Field label="Contact Number" required>
              <input name="contactNumber" value={form.contactNumber} onChange={handleChange}
                className="luxury-input" placeholder="+91 98765 43210" required />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Residential Address" required>
              <textarea name="residentialAddress" value={form.residentialAddress} onChange={handleChange}
                className="luxury-input resize-none" rows={3} placeholder="Full residential address..." required />
            </Field>
          </div>
        </div>

        {/* Documents */}
        <div className="luxury-card p-6 mb-6">
          <h2 className=" text-lg text-slate-800 mb-5 flex items-center gap-2">
            <span className="text-xs  text-slate-800 bg-obsidian-700 px-2 py-1 rounded">03</span>
            Documents
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Aadhar Card Photocopy" required>
              <FileUploadBox label="Upload Aadhar Card" name="aadharCard"
                accept=".jpg,.jpeg,.png,.pdf" file={files.aadharCard}
                onChange={handleFile} icon={HiOutlineDocumentText} />
            </Field>
            <Field label="Passport Size Photo" required>
              <FileUploadBox label="Upload Passport Photo" name="passportPhoto"
                accept=".jpg,.jpeg,.png" file={files.passportPhoto}
                onChange={handleFile} icon={HiOutlinePhotograph} />
            </Field>
          </div>
        </div>

        {/* Consent + Submit */}
        <div className="flex items-center justify-between">
          <p className="text-slate-500 text-xs max-w-sm ml-2">
            By submitting, you confirm the information is accurate and consent to data processing.
          </p>
          <button type="submit" disabled={loading} className="btn-gold px-10 py-3 text-base">
            {loading ? 'Submitting...' : 'Submit Registration'}
          </button>
        </div>
      </form>
    </div>
  );
}
